import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Language {
  code: string;
  name: string;
  domain: string;
  locale: string;
}

const languages: Language[] = [
  { code: 'is', name: 'Íslenska', domain: 'vefsida.is', locale: 'is-IS' },
  { code: 'en', name: 'English', domain: 'vefsida.io', locale: 'en-US' },
  { code: 'es', name: 'Español', domain: 'vefsida.es', locale: 'es-ES' },
  { code: 'da', name: 'Dansk', domain: 'vefsida.dk', locale: 'da-DK' }
];

const LanguagePicker = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { i18n } = useTranslation();
  const currentDomain = window.location.hostname;
  const currentLanguage = languages.find(lang => lang.domain === currentDomain) || languages[0];

  const handleLanguageChange = (language: Language) => {
    if (language.domain !== currentDomain) {
      // Change language immediately
      i18n.changeLanguage(language.code);
      
      // Construct new URL with the target domain
      const newUrl = new URL(window.location.href);
      newUrl.hostname = language.domain;
      
      // Redirect to the new domain
      window.location.href = newUrl.toString();
    }
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 text-[15px] font-medium rounded-full text-gray-600 hover:text-[#EA295A] hover:bg-gray-50 transition-colors"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Globe className="w-4 h-4" />
        <span>{currentLanguage.name}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-lg ring-1 ring-black/5 overflow-hidden z-50"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="py-2">
              {languages.map((language) => (
                <motion.button
                  key={language.code}
                  onClick={() => handleLanguageChange(language)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center space-x-2 ${
                    language.domain === currentDomain
                      ? 'text-[#EA295A] font-medium'
                      : 'text-gray-600'
                  }`}
                  whileHover={{ x: 5 }}
                >
                  <Globe className="w-4 h-4" />
                  <span>{language.name}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguagePicker;